/* PendenciasPanel — painel lateral (sticky desktop / drawer mobile)
 * com lista de pendências em tempo real do cadastro.
 */
import { useState, useMemo } from "react";
import { AlertOctagon, AlertTriangle, CheckCircle2, ChevronUp, ChevronDown, ListChecks } from "lucide-react";
import { computeCadastroState, STATE_META } from "@/lib/cadastroValidation";

export default function PendenciasPanel({ form, onJump }) {
  const [openMobile, setOpenMobile] = useState(false);
  const state = useMemo(() => computeCadastroState(form), [form]);
  const total = state.pendencies.length + state.warnings.length;

  return (
    <>
      {/* DESKTOP — sticky lateral */}
      <aside
        className="hidden xl:block sticky top-24 self-start space-y-3"
        data-testid="pendencias-panel-desktop"
      >
        <Panel state={state} onJump={onJump} />
      </aside>

      {/* MOBILE — flutuante recolhível inferior */}
      <div
        className="xl:hidden fixed bottom-4 right-4 z-40 w-[min(90vw,360px)] shadow-xl"
        data-testid="pendencias-panel-mobile"
      >
        <button
          type="button"
          onClick={() => setOpenMobile((v) => !v)}
          className={`w-full flex items-center justify-between px-3 py-2 rounded-t-lg border-x border-t font-semibold text-sm ${
            total > 0
              ? "bg-amber-500 text-white border-amber-600"
              : "bg-emerald-600 text-white border-emerald-700"
          } ${openMobile ? "" : "rounded-b-lg border-b"}`}
          data-testid="pendencias-toggle-mobile"
        >
          <span className="flex items-center gap-2">
            <ListChecks size={14} />
            {total === 0 ? "Sem pendências" : `${total} pendência(s)`}
          </span>
          {openMobile ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
        </button>
        {openMobile && (
          <div className="bg-[var(--surface)] border border-amber-600 border-t-0 rounded-b-lg p-3 max-h-[60vh] overflow-y-auto">
            <Panel state={state} onJump={onJump} hideHeader />
          </div>
        )}
      </div>
    </>
  );
}

function Panel({ state, onJump, hideHeader }) {
  return (
    <div className="card-mil p-4 space-y-3" data-testid="pendencias-panel-content">
      {!hideHeader && (
        <div className="flex items-center justify-between">
          <div>
            <div className="label-mil text-[var(--gold)]">Painel de Pendências</div>
            <div className="font-display font-bold text-base">Cadastro · {state.progress}%</div>
          </div>
          {state.canSubmit && <CheckCircle2 size={20} className="text-emerald-500" />}
        </div>
      )}

      {/* Resumo por etapa */}
      <ul className="space-y-1.5" data-testid="pendencias-steps">
        {state.steps.map((s) => {
          const meta = STATE_META[s.state];
          return (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => onJump?.(s.anchor)}
                className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded border text-[12px] ${meta.bg} ${meta.border} ${meta.text} hover:shadow-sm transition`}
                data-testid={`pendencias-step-${s.id}`}
              >
                <span className="font-semibold">{s.label}</span>
                <span className="text-[10px] uppercase">{meta.label}</span>
              </button>
            </li>
          );
        })}
      </ul>

      {/* Pendências e Avisos */}
      <div className="pt-3 border-t border-[var(--border)] space-y-1.5">
        {state.pendencies.length === 0 && state.warnings.length === 0 && (
          <p className="text-[12px] text-emerald-700 dark:text-emerald-300 flex items-start gap-1.5">
            <CheckCircle2 size={12} className="mt-0.5 shrink-0" />
            Nenhuma pendência crítica.
          </p>
        )}
        {state.pendencies.map((p) => (
          <p key={`p-${p}`} className="text-[12px] text-rose-700 dark:text-rose-300 flex items-start gap-1.5">
            <AlertOctagon size={12} className="mt-0.5 shrink-0" /> {p}
          </p>
        ))}
        {state.warnings.map((w) => (
          <p key={`w-${w}`} className="text-[12px] text-amber-700 dark:text-amber-300 flex items-start gap-1.5">
            <AlertTriangle size={12} className="mt-0.5 shrink-0" /> {w}
          </p>
        ))}
      </div>
    </div>
  );
}
